import { useEffect } from 'react'

import { ensurePusher } from 'services/pusherclient/pusherClient'
import { useStreamingStore } from 'store/streaming'
import { useIvsChatStore } from 'store/useIvsChatStore'
import { useViewerStore } from 'store/viewer'

type LiveChatPusherArgs = {
  token: string | null
  currentUserId?: string | number | null
  streamId?: string | number | null
}

type PresenceMemberInfo = {
  id?: string | number
  user_id?: string | number
  username?: string
  avatar_url?: string | null
  is_creator?: boolean
  stream_id?: string | number
}

type PusherPresenceChannel = {
  bind: (eventName: string, callback: (data: any) => void) => void
  unbind: (eventName: string, callback?: (data: any) => void) => void
  bind_global: (callback: (eventName: string, data: any) => void) => void
  unbind_global: (callback: (eventName: string, data: any) => void) => void
  members?: {
    count?: number
    me?: { id: string | number; info?: PresenceMemberInfo }
    members?: Record<string, { id: string | number; info?: PresenceMemberInfo }>
    each?: (callback: (member: { id: string | number; info?: PresenceMemberInfo }) => void) => void
    get?: (id: string | number) => { id: string | number; info?: PresenceMemberInfo } | null
  }
}

export const useLiveChatPusher = ({
  token,
  currentUserId,
  streamId,
}: LiveChatPusherArgs) => {
  useEffect(() => {
    const userId = currentUserId ? String(currentUserId) : ''
    const activeStream = streamId ? String(streamId) : ''

    if (!token || !activeStream) return

    const pusher = ensurePusher(token)
    if (!pusher) {
      return
    }

    const userChannelName = userId ? `private-livechat.user.${userId}` : ''
    const streamChannelName = `presence-stream.${activeStream}`

    const userChannel = userChannelName ? pusher.subscribe(userChannelName) : null
    const streamChannel = pusher.subscribe(streamChannelName) as PusherPresenceChannel

    const setViewerCountEverywhere = (count: number) => {
      const safeCount = Number.isFinite(count) ? Math.max(0, count) : 0
      useStreamingStore.getState().setViewerCount(safeCount)
      useViewerStore.getState().setViewerCount(safeCount)
    }

    const getPresenceCount = () => {
      const count = Number(streamChannel?.members?.count ?? 0)
      return Number.isFinite(count) ? Math.max(0, count) : 0
    }

    const appendSystemMessage = (
      streamKey: string,
      nextId: string,
      content: string,
      username: string,
      avatarUrl: string,
      senderUserId: string,
      extra: Record<string, any> = {}
    ) => {
      useIvsChatStore.setState((s) => {
        const current = s.messagesByStream[streamKey] ?? []

        if (current.some((m) => String(m.id) === nextId)) {
          return s
        }

        const nextMessage = {
          id: nextId,
          content,
          sender: {
            userId: senderUserId,
          },
          username,
          avatar_url: avatarUrl,
          to_user: null,
          sendTime: new Date(),
          isOwner: false,
          ...extra,
        } as any

        return {
          messagesByStream: {
            ...s.messagesByStream,
            [streamKey]: [...current, nextMessage],
          },
        }
      })
    }

    const onUserSubscriptionSucceeded = () => {}

    const onUserSubscriptionError = () => {}

    const onStreamSubscriptionSucceeded = () => {
      const count = getPresenceCount()
      setViewerCountEverywhere(count)
    }

    const onStreamSubscriptionError = () => {}

    const onPresenceMemberAdded = (member: {
      id: string | number
      info?: PresenceMemberInfo
    }) => {
      const count = getPresenceCount()

      setViewerCountEverywhere(count)

      const memberUserId = String(
        member?.info?.user_id ?? member?.id ?? 'unknown'
      )
      const memberUsername = String(member?.info?.username ?? 'User')
      const memberAvatarUrl = member?.info?.avatar_url ?? ''

      const nextId = `presence-join-${memberUserId}-${activeStream}`

      appendSystemMessage(
        activeStream,
        nextId,
        `${memberUsername} joined`,
        memberUsername,
        memberAvatarUrl,
        memberUserId,
        { isJoin: true }
      )
    }

    const onPresenceMemberRemoved = () => {
      const count = getPresenceCount()
      setViewerCountEverywhere(count)
    }

    const onPrivateMessage = (payload: any) => {
      if (String(payload?.stream_id ?? '') !== activeStream) {
        return
      }

      useIvsChatStore.setState((s) => {
        const current = s.messagesByStream[activeStream] ?? []
        const nextId = String(payload?.id ?? `private-${Date.now()}`)

        if (current.some((m) => String(m.id) === nextId)) {
          return s
        }

        const nextMessage = {
          id: nextId,
          content: String(payload?.content ?? ''),
          sender: {
            userId: String(payload?.user_id ?? ''),
          },
          username: String(payload?.username ?? ''),
          avatar_url: payload?.avatar_url ?? '',
          to_user: payload?.to_user != null ? String(payload.to_user) : null,
          sendTime: payload?.created_at ? new Date(payload.created_at) : new Date(),
          isOwner:
            String(payload?.user_id ?? '') === String(s.streamOwnerId ?? ''),
        } as any

        return {
          messagesByStream: {
            ...s.messagesByStream,
            [activeStream]: [...current, nextMessage],
          },
        }
      })
    }

    const onTipStream = (payload: any) => {
      if (String(payload?.stream_id ?? '') !== activeStream) {
        return
      }

      const amount =
        typeof payload?.amount === 'number'
          ? payload.amount
          : Number(payload?.amount ?? 0)

      if (Number.isFinite(amount) && amount > 0) {
        const streamingState = useStreamingStore.getState()
        const viewerState = useViewerStore.getState()

        streamingState.incrementTipsAmount(amount)
        viewerState.incrementTipsAmount(amount)

        const creatorGoal = streamingState.currentStreamGoal
        const creatorGoalStreamId =
          creatorGoal?.stream_id != null ? String(creatorGoal.stream_id) : null

        if (creatorGoal && creatorGoalStreamId === activeStream) {
          streamingState.incrementCurrentStreamGoalTipped(amount)
        }

        const watcherGoal = viewerState.currentStreamGoal
        const watcherGoalStreamId =
          watcherGoal?.stream_id != null ? String(watcherGoal.stream_id) : null

        if (watcherGoal && watcherGoalStreamId === activeStream) {
          const nextAmountTipped = Number(watcherGoal.amount_tipped ?? 0) + amount
          const amountGoal =
            watcherGoal.amount_goal != null
              ? Number(watcherGoal.amount_goal)
              : null

          const remaining =
            amountGoal != null
              ? Math.max(amountGoal - nextAmountTipped, 0)
              : null

          const percent =
            amountGoal != null && amountGoal > 0
              ? Math.min((nextAmountTipped / amountGoal) * 100, 100)
              : null

          viewerState.setCurrentStreamGoal({
            ...watcherGoal,
            amount_tipped: nextAmountTipped,
            remaining,
            percent,
          })
        }
      }

      const nextId = String(
        payload?.id ??
          `tip-stream-${String(payload?.sender_id ?? 'unknown')}-${String(
            payload?.created_at ?? Date.now()
          )}-${activeStream}`
      )

      const formattedAmount = Number.isFinite(amount)
        ? amount.toFixed(2).replace(/\.00$/, '')
        : '0'

      appendSystemMessage(
        activeStream,
        nextId,
        String(payload?.message ?? '').trim() ||
          `${String(payload?.sender_username ?? 'User')} tipped you $${formattedAmount}`,
        String(payload?.sender_username ?? 'User'),
        payload?.sender_avatar_url ?? '',
        String(payload?.sender_id ?? ''),
        { isTip: true }
      )
    }

    const onStreamGoalPublished = (payload: any) => {
      if (String(payload?.stream_id ?? '') !== activeStream) {
        return
      }

      const normalizedGoal = {
        stream_id: String(payload?.stream_id),
        user_id: Number(payload?.user_id),
        goal_id: Number(payload?.goal_id),
        amount_goal:
          payload?.amount_goal != null ? Number(payload.amount_goal) : null,
        amount_tipped: Number(payload?.amount_tipped ?? 0),
        remaining:
          payload?.remaining != null ? Number(payload.remaining) : null,
        percent: payload?.percent != null ? Number(payload.percent) : null,
        reward: payload?.reward ?? null,
        public: Boolean(payload?.public),
        completed: Boolean(payload?.completed),
      }

      useStreamingStore.getState().setCurrentStreamGoal(normalizedGoal as any)
      useViewerStore.getState().setCurrentStreamGoal(normalizedGoal as any)
    }

    const onUserEvent = (eventName: string, data: any) => {
      if (eventName?.startsWith('pusher:')) return

      if (eventName === 'livechat.private.message.sent') {
        onPrivateMessage(data)
      }
    }

    const onStreamEvent = (eventName: string, data: any) => {
      if (eventName?.startsWith('pusher:')) return

      if (eventName === 'tip-stream') {
        onTipStream(data)
        return
      }

      if (eventName === 'stream-goal-published') {
        onStreamGoalPublished(data)
      }
    }

    if (userChannel) {
      userChannel.bind('pusher:subscription_succeeded', onUserSubscriptionSucceeded)
      userChannel.bind('pusher:subscription_error', onUserSubscriptionError)
      userChannel.bind_global(onUserEvent)
    }

    streamChannel.bind('pusher:subscription_succeeded', onStreamSubscriptionSucceeded)
    streamChannel.bind('pusher:subscription_error', onStreamSubscriptionError)
    streamChannel.bind('pusher:member_added', onPresenceMemberAdded)
    streamChannel.bind('pusher:member_removed', onPresenceMemberRemoved)
    streamChannel.bind_global(onStreamEvent)

    return () => {
      try {
        if (userChannel) {
          userChannel.unbind(
            'pusher:subscription_succeeded',
            onUserSubscriptionSucceeded
          )
          userChannel.unbind(
            'pusher:subscription_error',
            onUserSubscriptionError
          )
          userChannel.unbind_global(onUserEvent)
          pusher.unsubscribe(userChannelName)
        }

        streamChannel.unbind(
          'pusher:subscription_succeeded',
          onStreamSubscriptionSucceeded
        )
        streamChannel.unbind(
          'pusher:subscription_error',
          onStreamSubscriptionError
        )
        streamChannel.unbind('pusher:member_added', onPresenceMemberAdded)
        streamChannel.unbind('pusher:member_removed', onPresenceMemberRemoved)
        streamChannel.unbind_global(onStreamEvent)
        pusher.unsubscribe(streamChannelName)
      } catch (err) {}
    }
  }, [token, currentUserId, streamId])
}